import React from "react";
import { GameCanvas } from "./GameCanvas";
import { UserInterface } from "./UserInterface";
import { Player } from "../../../shared/player";
import { GameClient, ImmuneCellInspection } from "../gameClient";
import { css, Global } from "@emotion/react";
import "../palettes/colorPalette"
import { CameraZoomLevel } from "../../../shared/constants";
import { ScreenOverlay } from "./ScreenOverlay";
import { TrainingLevel } from "../../../shared/playerAccount";
import { getQueryParams } from "../../../shared/utils";
import { HostDisconnectedBanner } from "./HostDisconnectedBanner";
import { ClientDisconnectedBanner } from "./ClientDisconnectedBanner";
import { WebWorkerClient } from "../networking/connectionToServer";
import { onSteamInvite } from "../networking/pendingSteamInvite";

declare var gameClient: GameClient;

export class App extends React.Component<{}, {
	player: Player | null,
	round: number,
	currentFunds: number,
	maxFunds: number,
	cells: Array<any>,
	hp: number,
	maxCapacity: number,
	offer: Array<any>,
	rewardOffer: Array<any>,
	experience: number,
	opponents: Array<[number, number, number, string, number, number]>,
	winners: Array<Player>,
	inspectedImmuneCell: ImmuneCellInspection | null,
	draggedImmuneCell: boolean,
	gainedMoney: number,
	zoomLevel: number,
	hideMarket: boolean,
	trainingUpdated: boolean,
	enabledButtons?: string,
	hostId?: string,
	peerConnected: boolean,
	isSpawnGame?: boolean,
	isReconnecting?: boolean,
	hostReconnectFailed?: boolean,
	opponentDisconnected?: boolean,
}> {
	private unsubscribeSteamInvite?: () => void;

	constructor(props: any) {
		super(props);

		this.handleChange = this.handleChange.bind(this);
		if (!(window as any).gameClient) {
			(window as any).gameClient = new GameClient(this);
		}

		const queryParams = getQueryParams(window.location.href);
		const lobbyToJoin = queryParams['lobby'];

		this.state = {
			player: null,
			round: 0,
			currentFunds: 0,
			maxFunds: 0,
			cells: [],
			hp: 0,
			maxCapacity: 0,
			offer: [],
			rewardOffer: [],
			experience: 0,
			opponents: [],
			winners: [],
			inspectedImmuneCell: null,
			draggedImmuneCell: false,
			gainedMoney: 0,
			zoomLevel: CameraZoomLevel.Normal,
			hideMarket: false,
			trainingUpdated: false,
			enabledButtons: undefined,
			hostId: lobbyToJoin,
			isSpawnGame: lobbyToJoin !== undefined,
			peerConnected: false,
			isReconnecting: false,
			opponentDisconnected: false,
		}
	}

	componentDidMount() {
		// the lobby pick up the pending invite.
		this.unsubscribeSteamInvite = onSteamInvite(() => {
			if (gameClient.getCurrentState() !== 'lobby') {
				gameClient.goToLobby(false);
			}
		});
	}

	componentWillUnmount() {
		this.unsubscribeSteamInvite?.();
	}

	initClient() {
		const canvas = document.querySelector("canvas")!;
		const uiRegion = document.querySelector(".ui")! as HTMLElement;
		gameClient.initializeGraphicsAndSound(canvas, uiRegion);
	}

	handleChange() {
		this.forceUpdate();
	}

	updateRound(round: number) {
		this.setState({ round });
	}

	updateFunds(currentFunds: number, maxFunds: number) {
		this.setState({ currentFunds, maxFunds });
	}

	updateOffer(offer: Array<any>) {
		this.setState({ offer });
	}

	render() {
		return (
			<>
				<Global
					styles={[
						{
							"@font-face": {
								fontFamily: "NotoSans",
								src: 'url("assets/fonts/NotoSans/NotoSans-Regular.ttf") format("truetype")'
							}
						},
						{
							"@font-face": {
								fontFamily: "NotoSansDisplay",
								src: 'url("assets/fonts/NotoSansDisplay/NotoSansDisplay-Regular.ttf") format("truetype")'
							}
						},
						{
							"@font-face": {
								fontFamily: "NotoSerif",
								src: 'url("assets/fonts/NotoSerif/NotoSerif-Regular.ttf") format("truetype")'
							}
						},
					]}
				/>
				<GameCanvas onReady={() => { this.initClient(); }} />
				<UserInterface
					gameClient={(window as any).gameClient}
					round={this.state.round}
					currentFunds={this.state.currentFunds || 0}
					maxFunds={this.state.maxFunds || 0}
					cells={this.state.player?.immuneCells || []}
					hp={this.state.player?.hp || 0}
					maxCapacity={this.state.player?.maxCapacity || 0}
					offer={this.state.player?.offer || []}
					rewardOffer={this.state.player?.rewardOffer || []}
					rewardOptions={this.state.player?.rewardOptions || []}
					experience={this.state.player?.experience || 0}
					level={this.state.player?.level || 0}
					opponents={this.state.opponents}
					winners={this.state.winners}
					inspectedImmuneCell={this.state.inspectedImmuneCell}
					draggedImmuneCell={this.state.draggedImmuneCell}
					gainedMoney={this.state.gainedMoney}
					zoomLevel={this.state.zoomLevel}
					hideMarket={this.state.hideMarket}
					trainingUpdated={this.state.trainingUpdated}
					enabledButtons={this.state.enabledButtons}
					hostId={this.state.hostId}
					isSpawnGame={this.state.isSpawnGame || false}
					peerConnected={this.state.peerConnected || false}
				/>
				{(this.state.opponentDisconnected || this.state.hostReconnectFailed) && <HostDisconnectedBanner />}
				{this.state.isReconnecting && <ClientDisconnectedBanner />}
			</>
		);
	}
}
